import profileApi from "@apis/profile.api";
import store from "@plugins/store";

// cache TTL: 2 minutes

const HEARTBEAT_INTERVAL_MS = 1 * 60 * 1000; // 1 minute interval for sending heartbeats
const FETCH_INTERVAL_MS = 1000; // 1 second interval for fetching online status
const RECENT_PING_THRESHOLD_MS = 1 * 60 * 1000; // 1 minute threshold for determining recent pings
const LAST_ONLINE_THRESHOLD_MS = 2 * 60 * 1000; // 2 minutes threshold for offline status
const FETCH_BATCH_SIZE = 20; // number of users to fetch in each batch

interface UserOnlineStatus {
  lastPingTime: Date;
  lastOnlineTime: Date | null;
}

class UserOnlineService {
  private pendingUsers: Set<string> = new Set<string>();
  private userStatusMap: Record<string, UserOnlineStatus> = {};
  private heartBeatInterval: NodeJS.Timeout | null = null;
  private fetchStatusInterval: NodeJS.Timeout | null = null;

  constructor() {
    this.init();
  }

  private init(): void {
    this.setupVisibilityChangeListener();
    this.toggleFetchStatusInterval(true);
    this.toggleHeartBeat(true);
  }

  isUserOnline(userId: string): boolean {
    const currentUserId = store.getters["auth/userId"];
    if (currentUserId === userId) return true;

    const lastOnlineTime = this.userStatusMap[userId]?.lastOnlineTime ?? null;

    // If the user is not in the pending list and hasn't been recently pinged, add them
    if (!this.pendingUsers.has(userId) && !this.hasRecentlyPinged(userId)) {
      this.pendingUsers.add(userId);

      if (this.userStatusMap[userId]) {
        this.userStatusMap[userId].lastPingTime = new Date();
      } else {
        this.userStatusMap[userId] = {
          lastPingTime: new Date(),
          lastOnlineTime: null,
        };
      }
    }

    // has record and threshold is not reached
    if (
      lastOnlineTime &&
      Date.now() - new Date(lastOnlineTime).getTime() <= LAST_ONLINE_THRESHOLD_MS
    ) {
      return true;
    }

    return false;
  }

  private fetchOnlineStatus(): void {
    while (this.pendingUsers.size > 0) {
      const usersToFetch = Array.from(this.pendingUsers).splice(0, FETCH_BATCH_SIZE);
      usersToFetch.forEach((userId) => this.pendingUsers.delete(userId));

      profileApi.getOnlineStatusForUsers(usersToFetch).then((response) => {
        if (response.code !== "ok") return;

        response.data.forEach((user: { userId: string; lastOnlineTime: Date }) => {
          this.userStatusMap[user.userId].lastOnlineTime = user.lastOnlineTime;
        });
      });
    }
  }

  private sendHeartBeat(): void {
    if (document.visibilityState !== "visible") return;
    if (!store.getters["auth/isLogged"]) return;
    if (!store.getters["auth/isTeacher"] && !store.getters["auth/isStudent"]) return;
    profileApi.updateUserOnlineStatus().then((response) => {
      if (response.code !== "ok") return;

      const { userId, lastOnlineTime } = response.data;
      this.userStatusMap[userId] = {
        lastPingTime: new Date(),
        lastOnlineTime,
      };
    });
  }

  private hasRecentlyPinged(userId: string): boolean {
    const userStatus = this.userStatusMap[userId];
    if (!userStatus) return false;
    const timeSinceLastPing = Date.now() - userStatus.lastPingTime.getTime();
    return timeSinceLastPing < RECENT_PING_THRESHOLD_MS;
  }

  private toggleFetchStatusInterval(shouldStart: boolean): void {
    if (shouldStart) {
      if (this.fetchStatusInterval) return;

      this.fetchStatusInterval = setInterval(() => {
        if (this.pendingUsers.size > 0) {
          this.fetchOnlineStatus();
        }
      }, FETCH_INTERVAL_MS);
    } else {
      if (this.fetchStatusInterval) {
        clearInterval(this.fetchStatusInterval);
        this.fetchStatusInterval = null;
      }
    }
  }

  private toggleHeartBeat(shouldStart: boolean): void {
    if (shouldStart) {
      if (this.heartBeatInterval) return;

      // Send an immediate heartbeat
      this.sendHeartBeat();

      // Start periodic heartbeat
      this.heartBeatInterval = setInterval(() => {
        this.sendHeartBeat();
      }, HEARTBEAT_INTERVAL_MS);
    } else {
      if (this.heartBeatInterval) {
        clearInterval(this.heartBeatInterval);
        this.heartBeatInterval = null;
      }
    }
  }

  private setupVisibilityChangeListener(): void {
    document.addEventListener("visibilitychange", () => {
      const isVisible = document.visibilityState === "visible";
      this.toggleFetchStatusInterval(isVisible);
      this.toggleHeartBeat(isVisible);
    });
  }
}

const userOnlineService = new UserOnlineService();

export { userOnlineService };
